import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import axiosInstance from '../../axiosInstance';

/**
 * Component to display price value for music items
 * @param {Object} props - Component props
 * @param {Object} props.item - Music item data
 * @returns {JSX.Element} Music price value component
 */
const MusicPriceValue = ({ item }) => {
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPriceData = async () => {
      if (!item || !item.name || !item.artist) return;

      setLoading(true);
      setError(null);

      try {
        // This would be replaced with an actual API endpoint for music prices
        // For now, we'll just use a mock implementation
        // const response = await axiosInstance.get(`/music-prices?name=${encodeURIComponent(item.name)}&artist=${encodeURIComponent(item.artist)}`);
        
        // Mock price data based on the item's condition and format
        const mockPriceData = {
          value: calculateMockPrice(item),
          source: 'Estimated Value',
          lastUpdated: new Date().toISOString()
        };
        
        setPriceData(mockPriceData);
      } catch (err) {
        console.error('Error fetching music price data:', err);
        setError('Failed to load price data');
      } finally {
        setLoading(false);
      }
    };

    fetchPriceData();
  }, [item]);

  /**
   * Calculate a mock price based on item properties
   */
  const calculateMockPrice = (item) => {
    let basePrice = 10; // Default base price
    
    // Adjust based on format
    switch (item.format) {
      case 'Vinyl':
        basePrice = 15;
        break;
      case 'CD':
        basePrice = 8;
        break;
      case 'Cassette':
        basePrice = 5;
        break;
      case '8-Track':
        basePrice = 20; // Vintage value
        break;
      case 'Box Set':
        basePrice = 30;
        break;
      default:
        basePrice = 10;
    }
    
    // Adjust based on condition
    if (item.condition) {
      if (item.condition.includes('Mint') || item.condition.includes('Sealed') || item.condition.includes('New')) {
        basePrice *= 1.5;
      } else if (item.condition.includes('Good')) {
        basePrice *= 0.8;
      } else if (item.condition.includes('Poor')) {
        basePrice *= 0.5;
      }
    }
    
    // Adjust based on edition
    if (item.edition) {
      if (item.edition.includes('Limited') || item.edition.includes('Collector') || item.edition.includes('First')) {
        basePrice *= 1.3;
      }
    }
    
    // Round to 2 decimal places
    return Math.round(basePrice * 100) / 100;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={16} sx={{ mr: 1 }} />
        <Typography variant="body2">Loading price...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (!priceData) {
    return (
      <Typography variant="body2" color="text.secondary">
        No price data available
      </Typography>
    );
  }

  return (
    <Tooltip title={`Source: ${priceData.source}, Last updated: ${new Date(priceData.lastUpdated).toLocaleDateString()}`}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        ${priceData.value.toFixed(2)}
      </Typography>
    </Tooltip>
  );
};

export default MusicPriceValue;
